import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { FC, useState } from "react";

import { Headline, Small } from "@fronterahealth/frontera-ui-components";

import { ConnectedObjectBadges } from "@components/ConnectedObjectBadges/ConnectedObjectBadges";
import { SuggestionCriteriaForm } from "@pages/QASettings/SuggestionCriteriaForm";

interface CriteriaCardCriteria {
  id: string;
  enabled: boolean;
  suggestionCriteria: {
    id: string;
    name: string;
    category: string;
    content: string;
  };
}

interface CriteriaCardProps {
  criteria: CriteriaCardCriteria;
  relatedNames?: string[];
  relatedType?: string;
  onToggleEnabled?: (id: string, enabled: boolean) => Promise<void>;
  onContentSave: (id: string, content: string) => Promise<void>;
  onCategorySave: (id: string, category: string) => Promise<void>;
  readOnly?: boolean;
}

export const CriteriaCard: FC<CriteriaCardProps> = ({
  criteria,
  relatedNames = [],
  relatedType = "orgs",
  onToggleEnabled,
  onContentSave,
  onCategorySave,
  readOnly = false,
}) => {
  const [isToggling, setIsToggling] = useState(false);
  const criteriaId = criteria.id;
  const criteriaData = criteria.suggestionCriteria;

  const handleToggle = async (enabled: boolean) => {
    if (isToggling || !onToggleEnabled) return;
    setIsToggling(true);
    try {
      await onToggleEnabled(criteriaId, enabled);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-6 shadow-xs">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Headline size="medium">{criteriaData.name}</Headline>
          <ConnectedObjectBadges names={relatedNames} type={relatedType} />
          {isToggling && (
            <span className="flex items-center gap-1 text-sm text-gray-400">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Saving
            </span>
          )}
        </div>
        {onToggleEnabled && (
          <label
            className={`flex items-center gap-2 ${isToggling ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
          >
            <Small>Enabled</Small>
            <input
              type="checkbox"
              className="h-4 w-4 rounded-sm border-gray-300 text-hatch-600 focus:ring-hatch-600"
              checked={criteria.enabled}
              disabled={isToggling}
              onChange={(e) => handleToggle(e.target.checked)}
            />
          </label>
        )}
      </div>

      <SuggestionCriteriaForm
        criteria={criteriaData}
        onContentSave={(content) => onContentSave(criteriaId, content)}
        onCategorySave={(category) => onCategorySave(criteriaId, category)}
        readOnly={readOnly}
      />
    </div>
  );
};
